import { useCallback, useEffect, useRef, useState } from 'react';

import {
  describeFailure,
  openDocument,
  receiveEnvelope,
  verifyEnvelope,
  type AcceptedPiiNoticeVersion,
  type FailureDescription,
  type OpenedDocument,
  type VerifiedEnvelope,
  type ViewerConfig,
} from './core';
import { getIdToken } from './idToken';

export type SessionState =
  | { name: 'unconfigured' }
  | { name: 'receiving' }
  | { name: 'unusable'; failure: FailureDescription }
  | { name: 'ready'; saleKey: string }
  | { name: 'opening' }
  | { name: 'notice' }
  | { name: 'open'; saleKey: string; document: OpenedDocument; suspended: boolean }
  | { name: 'stopped'; saleKey: string; failure: FailureDescription };

// api serves the notice only to a bidder with Level 2 access now, so opening again can't help.
export const NOTICE_REFUSED_FAILURE: FailureDescription = {
  title: "This document can't be opened",
  body: "You don't have Level 2 access to an open sale now, so this Level 2 document can't be opened.",
  next: 'none',
};

export function useViewerSession(config: ViewerConfig | undefined) {
  const [state, setState] = useState<SessionState>(
    config ? { name: 'receiving' } : { name: 'unconfigured' },
  );
  const verified = useRef<VerifiedEnvelope | undefined>(undefined);
  const attempt = useRef<AbortController | undefined>(undefined);
  const opened = useRef<OpenedDocument | undefined>(undefined);
  const answerNotice = useRef<((version: AcceptedPiiNoticeVersion | null) => void) | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!config) {
      return undefined;
    }
    let unmounted = false;
    const hand = new AbortController();

    (async () => {
      try {
        const raw = await receiveEnvelope(window, hand.signal);
        const envelope = await verifyEnvelope(raw, config.headerSigningKeys);
        if (unmounted) {
          return;
        }
        verified.current = envelope;
        setState({ name: 'ready', saleKey: envelope.header.saleKey });
      } catch (error) {
        if (!unmounted) {
          setState({ name: 'unusable', failure: describeFailure(error) });
        }
      }
    })();

    return () => {
      unmounted = true;
      hand.abort();
      attempt.current?.abort();
      answerNotice.current?.(null);
      opened.current?.close();
    };
  }, [config]);

  const open = useCallback(async () => {
    const envelope = verified.current;
    if (!envelope || !config) {
      return;
    }
    const { saleKey } = envelope.header;
    attempt.current?.abort();
    const mine = new AbortController();
    attempt.current = mine;
    let lockedWhileOpening = false;
    let suspended = false;
    setState({ name: 'opening' });

    try {
      const document = await openDocument(envelope, {
        config,
        getIdToken,
        signal: mine.signal,
        askPiiNotice: () =>
          new Promise<AcceptedPiiNoticeVersion | null>((resolve) => {
            answerNotice.current = resolve;
            setState({ name: 'notice' });
          }),
        onSuspend: () => {
          if (mine.signal.aborted) {
            return;
          }
          suspended = true;
          setState((current) =>
            current.name === 'open' ? { ...current, suspended: true } : current,
          );
        },
        onResume: () => {
          if (mine.signal.aborted) {
            return;
          }
          suspended = false;
          setState((current) =>
            current.name === 'open' ? { ...current, suspended: false } : current,
          );
        },
        onLock: (why) => {
          if (mine.signal.aborted) {
            return;
          }
          lockedWhileOpening = true;
          opened.current = undefined;
          setState({ name: 'stopped', saleKey, failure: describeFailure(why) });
        },
      });
      if (mine.signal.aborted || lockedWhileOpening) {
        document.close();
        return;
      }
      opened.current = document;
      setState({ name: 'open', saleKey, document, suspended });
    } catch (error) {
      if (!mine.signal.aborted) {
        setState({ name: 'stopped', saleKey, failure: describeFailure(error) });
      }
    }
  }, [config]);

  const acceptNotice = useCallback((version: AcceptedPiiNoticeVersion) => {
    answerNotice.current?.(version);
    answerNotice.current = undefined;
    setState({ name: 'opening' });
  }, []);

  const declineNotice = useCallback(() => {
    answerNotice.current?.(null);
    answerNotice.current = undefined;
  }, []);

  const closeRefusedNotice = useCallback(() => {
    attempt.current?.abort();
    answerNotice.current?.(null);
    answerNotice.current = undefined;
    const envelope = verified.current;
    if (envelope) {
      setState({
        name: 'stopped',
        saleKey: envelope.header.saleKey,
        failure: NOTICE_REFUSED_FAILURE,
      });
    }
  }, []);

  const backToReady = useCallback(() => {
    attempt.current?.abort();
    answerNotice.current?.(null);
    answerNotice.current = undefined;
    opened.current?.close();
    opened.current = undefined;
    const envelope = verified.current;
    if (envelope) {
      setState({ name: 'ready', saleKey: envelope.header.saleKey });
    }
  }, []);

  return {
    state,
    open,
    acceptNotice,
    declineNotice,
    closeRefusedNotice,
    close: backToReady,
    backToReady,
  };
}
